import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  Icon,
  Image,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  Flex,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Checkbox,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  Stack
} from '@chakra-ui/react';
import {
  FaChalkboardTeacher,
  FaUserGraduate,
  FaBook,
  FaGlobe,
  FaLightbulb,
  FaBullseye,
  FaChartLine,
  FaHome,
  FaEye,
  FaEnvelope,
  FaSignInAlt,
  FaWhatsapp,
  FaPhone,
  FaSearch,
  FaTimes,
  FaPlus,
  FaUsers
} from 'react-icons/fa';
import { MdSchool, MdEmail, MdLocationOn } from 'react-icons/md';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

// Dummy students list for demonstration
const STUDENTS = [
  { id: 1, name: 'محمد التونسي' },
  { id: 2, name: 'علي بن سالم' },
  { id: 3, name: 'فاطمة الزهراء' },
  { id: 4, name: 'سارة عبد الله' },
  { id: 5, name: 'طارق حسين' }
];

// About Page Content
const AboutContent = () => {
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const toast = useToast();

  // State for student management
  const [searchQuery, setSearchQuery] = useState('');
  const [absents, setAbsents] = useState([]); // student IDs for absent students
  const [group, setGroup] = useState([]); // student IDs for group
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedGroupStudentId, setSelectedGroupStudentId] = useState(null);

  // Search logic for "ابحث عن الطلاب"
  const filteredStudents = STUDENTS.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Search logic for "إضافة الطلاب إلى مجموعة"
  const filteredGroupStudents = STUDENTS.filter(student =>
    student.name.toLowerCase().includes(groupSearch.toLowerCase()) &&
    !group.includes(student.id) // prevent adding twice
  );

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Handler to toggle absent
  const toggleAbsent = (id) => {
    setAbsents(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Handler to add student to group
  const handleGroupAdd = (student) => {
    if (!group.includes(student.id)) {
      setGroup([...group, student.id]);
      // Optionally, show toast
      toast({
        title: `تمت إضافة ${student.name} إلى المجموعة`,
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    }
    setSelectedGroupStudentId(null);
  };

  // Handler to remove from group
  const handleGroupRemove = (id) => {
    setGroup(group.filter(gid => gid !== id));
  };

  return (
    <Box dir="rtl">
      {/* Hero Section */}
      <Box 
        bgGradient={`linear(to-r, ${secondaryColor}, gray.800)`} 
        color="white" 
        py={20}
        position="relative"
      >
        <Container maxW="6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} alignItems="center">
              <GridItem>
                <motion.div variants={fadeIn}>
                  <Heading size={headingSize} mb={6}>
                    عن <Box as="span" color={primaryColor}>منصتنا</Box>
                  </Heading>
                  <Text fontSize="xl" mb={8}>
                    اكتشف مهمتنا ورؤيتنا والتزامنا بالتعليم في تونس.
                  </Text>
                  <Button 
                    as={RouterLink}
                    to="/contact"
                    colorScheme="yellow" 
                    bg={primaryColor}
                    color={secondaryColor}
                    size="lg"
                  >
                    اتصل بنا
                  </Button>
                </motion.div>
              </GridItem>
              <GridItem>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <Image 
                    src="https://i.imgur.com/c2PqI8E.jpeg" 
                    alt="فريق تعليمي تونسي"
                    borderRadius="lg"
                    boxShadow="2xl"
                  />
                </motion.div>
              </GridItem>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Search Students Section */}
      <Box py={16} bg={bgColor}>
        <Container maxW="4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Heading size="lg" mb={8} color={secondaryColor} textAlign="center">
              ابحث عن الطلاب
            </Heading>
            <InputGroup size="lg" mb={8}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="ابحث بالاسم..."
                variant="filled"
                bg={cardBg}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                _focus={{ bg: "white" }}
                dir="rtl"
              />
            </InputGroup>

            {searchQuery && (
              <Box mt={2}>
                {filteredStudents.length === 0 ? (
                  <Text color="red.500" align="center">
                    لا يوجد طلاب مطابقون للبحث.
                  </Text>
                ) : (
                  <List spacing={3}>
                    {filteredStudents.map(student => (
                      <ListItem key={student.id}>
                        <HStack>
                          <Icon as={FaUserGraduate} color={primaryColor} />
                          <Text>{student.name}</Text>
                          <Checkbox
                            isChecked={absents.includes(student.id)}
                            colorScheme="red"
                            ml={2}
                            onChange={() => toggleAbsent(student.id)}
                          >
                            غائب؟
                          </Checkbox>
                          {/* Show red tag if absent */}
                          {absents.includes(student.id) && (
                            <Tag colorScheme="red" size="sm" variant="subtle" ml={2}>
                              <TagLabel>غائب</TagLabel>
                            </Tag>
                          )}
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* Add Students to Group Section */}
      <Box py={16} bg={cardBg}>
        <Container maxW="4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Heading size="lg" mb={8} color={secondaryColor} textAlign="center">
              <Icon as={FaUsers} mr={2} /> إضافة الطلاب إلى مجموعة
            </Heading>
            <InputGroup size="lg" mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="ابحث عن طالب لإضافته..."
                variant="filled"
                bg={bgColor}
                value={groupSearch}
                onChange={e => setGroupSearch(e.target.value)}
                _focus={{ bg: "white" }}
                dir="rtl"
              />
            </InputGroup>
            {groupSearch && (
              <Box mt={2}>
                {filteredGroupStudents.length === 0 ? (
                  <Text color="red.500" align="center">
                    لا يوجد طلاب متاحون لإضافتهم.
                  </Text>
                ) : (
                  <List spacing={3}>
                    {filteredGroupStudents.map(student =>
                      <ListItem key={student.id}>
                        <HStack>
                          <Icon as={FaUserGraduate} color={primaryColor} />
                          <Text>{student.name}</Text>
                          <Button
                            size="sm"
                            leftIcon={<FaPlus />}
                            colorScheme="green"
                            variant="outline"
                            onClick={() => handleGroupAdd(student)}
                          >
                            أضف إلى المجموعة
                          </Button>
                        </HStack>
                      </ListItem>
                    )}
                  </List>
                )}
              </Box>
            )}
            {/* Display current group */}
            <Box mt={8}>
              <Heading size="md" mb={3}>المجموعة الحالية:</Heading>
              {group.length === 0 ? (
                <Text color="gray.500" fontSize="sm">لا يوجد طلاب في المجموعة بعد.</Text>
              ) : (
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  {group.map(gid => {
                    const student = STUDENTS.find(s => s.id === gid);
                    return student ? (
                      <Tag key={gid} colorScheme="blue" size="md" borderRadius="full">
                        <TagLabel>{student.name}</TagLabel>
                        <TagCloseButton onClick={() => handleGroupRemove(gid)} />
                      </Tag>
                    ) : null;
                  })}
                </Stack>
              )}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box py={16} bg={bgColor}>
        <Container maxW="6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} alignItems="center">
              <GridItem>
                <Image 
                  src="https://i.imgur.com/c2PqI8E.jpeg" 
                  alt="مهمة تعليمية"
                  borderRadius="lg"
                  boxShadow="md"
                />
              </GridItem>
              <GridItem>
                <Heading size="xl" mb={6} color={secondaryColor}>
                  مهمتنا
                </Heading>
                <Text fontSize="lg" mb={6}>
                  نحن ملتزمون بتوفير تعليم عالي الجودة ومتاح لجميع الطلاب التونسيين، 
                  بما يتماشى مع البرامج الوطنية والاحتياجات الخاصة لنظامنا التعليمي.
                </Text>
                <List spacing={4}>
                  <ListItem>
                    <HStack align="flex-start">
                      <Icon as={FaLightbulb} color={primaryColor} mt={1} />
                      <Text>الابتكار التربوي المتكيف مع السياق التونسي</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack align="flex-start">
                      <Icon as={FaUserGraduate} color={primaryColor} mt={1} />
                      <Text>مرافقة شخصية لكل طالب</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack align="flex-start">
                      <Icon as={MdSchool} color={primaryColor} mt={1} />
                      <Text>التوافق التام مع برامج الوزارة</Text>
                    </HStack>
                  </ListItem>
                </List>
              </GridItem>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Vision Section */}
      <Box py={16}>
        <Container maxW="6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} alignItems="center">
              <GridItem order={{ base: 2, md: 1 }}>
                <Heading size="xl" mb={6} color={secondaryColor}>
                  رؤيتنا
                </Heading>
                <Text fontSize="lg" mb={6}>
                  نطمح لأن نصبح المرجع في مجال التعليم الرقمي في تونس، 
                  من خلال الجمع بين التميز الأكاديمي والابتكار التكنولوجي.
                </Text>
                <List spacing={4}>
                  <ListItem>
                    <HStack align="flex-start">
                      <Icon as={FaBullseye} color={primaryColor} mt={1} />
                      <Text>تقليل الفوارق في الوصول إلى التعليم الجيد</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack align="flex-start">
                      <Icon as={FaChartLine} color={primaryColor} mt={1} />
                      <Text>تحسين النتائج المدرسية على المستوى الوطني</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack align="flex-start">
                      <Icon as={FaGlobe} color={primaryColor} mt={1} />
                      <Text>إعداد الطلاب التونسيين لتحديات القرن الحادي والعشرين</Text>
                    </HStack>
                  </ListItem>
                </List>
              </GridItem>
              <GridItem order={{ base: 1, md: 2 }}>
                <Image 
                  src="/assets/education2.jpg" 
                  alt="رؤية تعليمية"
                  borderRadius="lg"
                  boxShadow="md"
                />
              </GridItem>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Values Section */}
      <Box py={16} bg={primaryColor} color={secondaryColor}>
        <Container maxW="6xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Heading size="xl" mb={12} textAlign="center">
              قيمنا
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <Box textAlign="center" p={6} bg={cardBg} borderRadius="lg">
                <Icon as={FaBook} w={12} h={12} color={primaryColor} mb={4} />
                <Heading size="md" mb={3}>التميز الأكاديمي</Heading>
                <Text>محتوى تعليمي دقيق يتوافق مع المعايير الوطنية</Text>
              </Box>
              <Box textAlign="center" p={6} bg={cardBg} borderRadius="lg">
                <Icon as={FaUserGraduate} w={12} h={12} color={primaryColor} mb={4} />
                <Heading size="md" mb={3}>المساواة</Heading>
                <Text>تكافؤ الفرص في الحصول على تعليم جيد لجميع الطلاب</Text>
              </Box>
              <Box textAlign="center" p={6} bg={cardBg} borderRadius="lg">
                <Icon as={FaChalkboardTeacher} w={12} h={12} color={primaryColor} mb={4} />
                <Heading size="md" mb={3}>الابتكار</Heading>
                <Text>أساليب تعليمية حديثة تتكيف مع السياق التونسي</Text>
              </Box>
            </SimpleGrid>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

// Main About Page Component
const AboutPage = () => {
  return (
    <Box>
      <Header />
      <AboutContent />
      <Footer />
    </Box>
  );
};

export default AboutPage;
